import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import BarChart from "@galaxy-io/dls/charts/BarChart";
import LineChart from "@galaxy-io/dls/charts/LineChart";
import StatChart, { StatChartVariant } from "@galaxy-io/dls/charts/StatChart";
import { ChartPalette } from "@galaxy-io/dls/charts/types";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Box, { BoxVariant } from "@galaxy-io/dls/layout/Box";
import DlsLink, { LinkSize } from "@galaxy-io/dls/links/Link";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import InfiniteTable, { InfiniteTableSize } from "@galaxy-io/dls/table/InfiniteTable";
import Text, { TextSize, TextWeight } from "@galaxy-io/dls/text/Text";
import Widget from "@galaxy-io/dls/widget/Widget";
import type { WebsiteOverview, WebsiteSessions } from "@gtm/contracts/integrations/posthog";
import { useQuery } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { type ReactNode, useState } from "react";
import { Link, useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/workspace/page-header";
import { Inset } from "@/components/workspace/people-styles";
import { MultiSelectField, SelectField } from "@/components/workspace/select-field";
import { TableFooter } from "@/components/workspace/table-footer";
import { TableLayout } from "@/components/workspace/table-layout";
import { Toolbar } from "@/components/workspace/toolbar";
import { Cards, Dashboard, Grid, Plot, TableBody } from "@/features/observability/dashboard-styles";
import { apiClient, unwrap } from "@/lib/api-client";
import { GithubMetricsView } from "./github-metrics";

export function ObservabilityView({ children }: { children: ReactNode }) {
  const { params } = useUrlState();
  const view = params.get("view");
  const github = view === "github";
  const isSessions = view === "website-sessions";
  const website = view === "website" || view === "website-visitors" || isSessions;
  function tabHref(view: string) {
    const next = new URLSearchParams();
    next.set("view", view);
    for (const key of ["connection", "domain", "days"]) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
    return `/observability?${next}`;
  }
  return (
    <>
      <PageHeader title="Observability" />
      <Box padding={[0, 12]}>
        <Tabs
          as={Link}
          ariaLabel="Observability sections"
          value={github ? "github" : website ? (isSessions ? "sessions" : "visitors") : "audience"}
          items={[
            { id: "audience", label: "Audience", href: "/observability" },
            { id: "visitors", label: "Website visitors", href: tabHref("website-visitors") },
            { id: "sessions", label: "Website sessions", href: tabHref("website-sessions") },
            { id: "github", label: "GitHub", href: "/observability?view=github" },
          ]}
        />
      </Box>
      {github ? (
        <GithubMetricsView />
      ) : website ? (
        <WebsiteMetrics isSessions={isSessions} />
      ) : (
        children
      )}
    </>
  );
}
function WebsiteMetrics({ isSessions }: { isSessions: boolean }) {
  const { params, update } = useUrlState();
  const connections = useQuery({
    queryKey: ["integrations"],
    queryFn: () => unwrap(apiClient().GET("/integrations", {})),
  });
  const available = connections.data?.filter((c) => c.provider === "posthog" && c.enabled) ?? [];
  const requested = params.get("connection");
  const selected = requested ? available.find((c) => c.id === requested) : available[0];
  const days =
    params.get("days") === "90"
      ? "90"
      : params.get("days") === "30"
        ? "30"
        : params.get("days") === "7"
          ? "7"
          : "14";
  const domain = params.get("domain") || undefined;
  const settings = useQuery({
    queryKey: ["posthog-config", selected?.id],
    enabled: !!selected,
    queryFn: () =>
      unwrap(
        apiClient().GET("/integrations/posthog/connections/{id}", {
          params: { path: { id: selected!.id } },
        }),
      ),
  });
  const overview = useQuery({
    queryKey: ["website-overview", selected?.id, days, domain],
    enabled: !!selected,
    queryFn: () =>
      unwrap(
        apiClient().GET("/reporting/website", {
          params: { query: { integrationId: selected!.id, days, ...(domain ? { domain } : {}) } },
        }),
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const sessions = useQuery({
    queryKey: ["website-sessions", selected?.id, days, domain],
    enabled: !!selected && isSessions,
    queryFn: () =>
      unwrap(
        apiClient().GET("/reporting/website/sessions", {
          params: { query: { integrationId: selected!.id, days, ...(domain ? { domain } : {}) } },
        }),
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (!connections.data)
    return (
      <QueryState
        error={connections.error}
        loadingMessage="Loading connections…"
        onRetry={() => void connections.refetch()}
      />
    );
  if (!available.length)
    return (
      <Inset>
        <Text>No active PostHog connection.</Text>
        <Link href="/integrations?integration=new-posthog">
          Connect PostHog to see website metrics
        </Link>
      </Inset>
    );
  return (
    <>
      <Toolbar>
        <SelectField
          compact
          label="Website connection"
          value={selected?.id || ""}
          placeholder="Choose a connection"
          options={available.map((c) => ({ value: c.id, label: c.name }))}
          onChange={(connection) => update({ connection, domain: null })}
          width={160}
          hideLabel
        />
        <MultiSelectField
          selectAll
          label="Domains"
          value={domain || ""}
          placeholder={settings.isPending ? "Loading domains…" : "All domains"}
          disabled={!settings.data}
          options={(settings.data?.domains ?? []).map((value) => ({ value, label: value }))}
          onChange={(domain) => update({ domain: domain || null })}
          width={200}
          showLabel={false}
        />
        <SelectField
          compact
          label="Date range"
          width={150}
          value={days}
          options={[
            { value: "7", label: "Last 7 days" },
            { value: "14", label: "Last 14 days" },
            { value: "30", label: "Last 30 days" },
            { value: "90", label: "Last 90 days" },
          ]}
          onChange={(days) => update({ days })}
          hideLabel
        />
        <Button
          label="Refresh"
          variant={ButtonVariant.SECONDARY}
          isDisabled={!selected || overview.isFetching || sessions.isFetching}
          onClick={() => {
            void overview.refetch();
            if (isSessions) void sessions.refetch();
          }}
          size={ButtonSize.SMALL}
        />
      </Toolbar>
      {settings.error && (
        <QueryState error={settings.error} onRetry={() => void settings.refetch()} />
      )}
      {!selected ? (
        <Inset>
          <Text>This connection is unavailable. Choose another website connection.</Text>
        </Inset>
      ) : overview.error || !overview.data ? (
        <QueryState
          error={overview.error}
          loadingMessage="Loading website metrics…"
          onRetry={() => void overview.refetch()}
        />
      ) : (
        <WebsiteContent
          key={isSessions ? "sessions" : "visitors"}
          isSessions={isSessions}
          overview={overview.data}
          sessions={sessions.data}
          sessionsError={sessions.error}
          onRetrySessions={() => void sessions.refetch()}
        />
      )}
    </>
  );
}
function duration(seconds: number | null | undefined) {
  if (seconds == null) return "—";
  const value = Math.round(seconds);
  return value >= 3600
    ? `${Math.floor(value / 3600)}h ${Math.floor((value % 3600) / 60)}m`
    : value >= 60
      ? `${Math.floor(value / 60)}m ${value % 60}s`
      : `${value}s`;
}
function WebsiteContent({
  overview,
  sessions,
  sessionsError,
  onRetrySessions,
  isSessions,
}: {
  overview: WebsiteOverview;
  sessions?: WebsiteSessions;
  sessionsError: Error | null;
  onRetrySessions: () => void;
  isSessions: boolean;
}) {
  const [comparisonMetric, setComparisonMetric] = useState<"pageviews" | "visitors" | "sessions">(
    isSessions ? "sessions" : "visitors",
  );
  const [sessionSearch, setSessionSearch] = useState("");
  const [replayFilter, setReplayFilter] = useState("all");
  const [minimumDuration, setMinimumDuration] = useState("0");
  const filteredSessions = (sessions?.rows ?? []).filter((row) => {
    const search = sessionSearch.trim().toLowerCase();
    return (
      (!search || `${row.entryPage} ${row.id}`.toLowerCase().includes(search)) &&
      (replayFilter === "all" ||
        (replayFilter === "available" ? !!row.replayUrl : !row.replayUrl)) &&
      (minimumDuration === "0" || row.duration > Number(minimumDuration))
    );
  });
  const stats = isSessions
    ? [
        ["Sessions", overview.metrics.sessions.toLocaleString()],
        ["Avg. session duration", duration(sessions?.metrics.averageDuration)],
        [
          "Bounce rate",
          sessions?.metrics.bounceRate == null
            ? "—"
            : `${(sessions.metrics.bounceRate * 100).toFixed(1)}%`,
        ],
        ["Pages per session", sessions?.metrics.pagesPerSession?.toFixed(1) ?? "—"],
      ]
    : [
        ["Unique visitors", overview.metrics.visitors.toLocaleString()],
        ["Pageviews", overview.metrics.pageviews.toLocaleString()],
        [
          "Pageviews per visitor",
          overview.metrics.visitors
            ? (overview.metrics.pageviews / overview.metrics.visitors).toFixed(1)
            : "—",
        ],
      ];
  return (
    <Dashboard>
      <Cards>
        {stats.map(([label, value]) => (
          <StatChart
            hasBorder
            key={label}
            label={label}
            variant={StatChartVariant.BASE}
            value={
              <Text size={TextSize.HEADING_MD} weight={TextWeight.MEDIUM}>
                {value}
              </Text>
            }
          />
        ))}
      </Cards>
      <Grid $columns={isSessions ? 3 : 2}>
        <Widget
          isFlush
          header={
            <Text weight={TextWeight.MEDIUM} as="h2">
              {isSessions ? "Daily sessions" : "Pageviews by domain"}
            </Text>
          }
        >
          <Plot>
            {isSessions ? (
              <Box height={200}>
                <BarChart
                  isFilterable
                  ariaLabel="Daily website sessions"
                  hasLegend={false}
                  series={{ sessions: { label: "Sessions", color: ChartPalette.PURPLE } }}
                  groups={overview.trend.map((day) => ({
                    label: formatInTimeZone(day.day, "UTC", "MMM d"),
                    bars: [
                      {
                        metric: "sessions" as const,
                        components: [
                          {
                            key: "sessions",
                            label: "Sessions",
                            value: day.sessions,
                            align: "right" as const,
                          },
                        ],
                      },
                    ],
                  }))}
                />
              </Box>
            ) : (
              <Box height={200}>
                <BarChart
                  isFilterable
                  ariaLabel="Daily website pageviews by domain"
                  hasLegend={overview.domains.length > 1}
                  series={Object.fromEntries(
                    overview.domains.map((domain) => [domain, { label: domain }]),
                  )}
                  groups={overview.trend.map((day) => ({
                    label: formatInTimeZone(day.day, "UTC", "MMM d"),
                    bars: overview.domains.map((domain) => ({
                      metric: domain,
                      components: [
                        { key: domain, label: domain, value: day.pageviewsByDomain[domain] ?? 0 },
                      ],
                    })),
                  }))}
                />
              </Box>
            )}
          </Plot>
        </Widget>
        <Widget
          isFlush
          header={
            <Text weight={TextWeight.MEDIUM} as="h2">
              {isSessions
                ? "Sessions vs previous period"
                : comparisonMetric === "visitors"
                  ? "Visitors vs previous period"
                  : "Pageviews vs previous period"}
            </Text>
          }
          actions={
            !isSessions && (
              <SelectField
                compact
                label="Comparison metric"
                value={comparisonMetric}
                width={120}
                options={[
                  { value: "visitors", label: "Visitors" },
                  { value: "pageviews", label: "Pageviews" },
                ]}
                onChange={(value) => setComparisonMetric(value as typeof comparisonMetric)}
              />
            )
          }
        >
          <Plot>
            <Box height={200}>
              <LineChart
                isFilterable
                ariaLabel="Website period comparison"
                aria-description="Selected period versus the preceding period, aligned by day. Today is incomplete."
                hasLegend
                series={{
                  current: {
                    label: `${formatInTimeZone(overview.from, "UTC", "MMM d")}–${formatInTimeZone(overview.to, "UTC", "MMM d")}`,
                    color: ChartPalette.LIME,
                  },
                  previous: {
                    label: `${formatInTimeZone(overview.comparison.from, "UTC", "MMM d")}–${formatInTimeZone(new Date(Date.parse(overview.comparison.to) - 86400000), "UTC", "MMM d")}`,
                    color: ChartPalette.ORANGE,
                  },
                }}
                labelFormatter={(day) => formatInTimeZone(day, "UTC", "MMM d")}
                lines={[
                  {
                    metric: "current",

                    points: overview.trend.map((day) => ({ x: day.day, y: day[comparisonMetric] })),
                  },
                  {
                    metric: "previous",
                    isDashed: true,
                    points: overview.trend.map((day, index) => ({
                      x: day.day,
                      y: overview.comparison.trend[index]?.[comparisonMetric] ?? null,
                    })),
                  },
                ]}
              />
            </Box>
          </Plot>
        </Widget>
        {isSessions && (
          <Widget
            isFlush
            header={
              <Text weight={TextWeight.MEDIUM} as="h2">
                Avg. session length vs previous period
              </Text>
            }
          >
            {!sessions ? (
              <QueryState
                error={sessionsError}
                loadingMessage="Loading session durations…"
                onRetry={onRetrySessions}
              />
            ) : (
              <Plot>
                <Box height={200}>
                  <LineChart
                    isFilterable
                    ariaLabel="Average session length comparison"
                    hasLegend
                    valueFormatter={duration}
                    labelFormatter={(day) => formatInTimeZone(day, "UTC", "MMM d")}
                    series={{
                      current: { label: "Selected period", color: ChartPalette.TEAL },
                      previous: { label: "Previous period", color: ChartPalette.ORANGE },
                    }}
                    lines={[
                      {
                        metric: "current",

                        points: overview.trend.map((day) => ({
                          x: day.day,
                          y:
                            sessions.durationTrend.find((point) => point.day === day.day)
                              ?.averageDuration ?? null,
                        })),
                      },
                      {
                        metric: "previous",
                        isDashed: true,
                        points: overview.trend.map((day, index) => ({
                          x: day.day,
                          y:
                            sessions.durationTrend.find(
                              (point) => point.day === overview.comparison.trend[index]?.day,
                            )?.averageDuration ?? null,
                        })),
                      },
                    ]}
                  />
                </Box>
              </Plot>
            )}
          </Widget>
        )}
      </Grid>
      {isSessions ? (
        <Widget
          isFlush
          header={
            <Text weight={TextWeight.MEDIUM} as="h2">
              Session replays
            </Text>
          }
        >
          <Toolbar
            trailingActions={
              sessionSearch || replayFilter !== "all" || minimumDuration !== "0" ? (
                <Button
                  label="Clear filters"
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => {
                    setSessionSearch("");
                    setReplayFilter("all");
                    setMinimumDuration("0");
                  }}
                  size={ButtonSize.SMALL}
                />
              ) : undefined
            }
          >
            <Input
              aria-label="Search session replays"
              placeholder="Search entry pages or session IDs…"
              value={sessionSearch}
              onChange={(event) => setSessionSearch(event)}
              type="search"
              size={InputSize.SMALL}
              label="Search session replays"
              hideLabel
            />
            <SelectField
              compact
              label="Replay availability"
              value={replayFilter}
              onChange={setReplayFilter}
              width={160}
              options={[
                { value: "all", label: "All sessions" },
                { value: "available", label: "With replay" },
                { value: "missing", label: "Without replay" },
              ]}
              hideLabel
            />
            <SelectField
              compact
              label="Minimum session duration"
              value={minimumDuration}
              onChange={setMinimumDuration}
              width={160}
              options={[
                { value: "0", label: "Any duration" },
                { value: "30", label: ">30 sec" },
                { value: "60", label: ">1 minute" },
                { value: "300", label: ">5 minute" },
                { value: "600", label: ">10 minute" },
              ]}
              hideLabel
            />
          </Toolbar>
          {sessions?.replayError && (
            <Toolbar>
              <Text size={TextSize.BODY_SM}>{sessions.replayError}</Text>
              <Button
                label="Retry replays"
                variant={ButtonVariant.SECONDARY}
                onClick={onRetrySessions}
                size={ButtonSize.SMALL}
              />
            </Toolbar>
          )}
          {!sessions ? (
            <QueryState
              error={sessionsError}
              loadingMessage="Loading sessions…"
              onRetry={onRetrySessions}
            />
          ) : (
            <TableBody>
              <TableLayout
                footer={
                  <TableFooter
                    label="Session replays"
                    total={filteredSessions.length}
                    actions={<Text size={TextSize.BODY_SM}>{sessions.rows.length} loaded</Text>}
                  />
                }
              >
                <Box variant={BoxVariant.BASE} height="100%">
                  <InfiniteTable
                    ariaLabel="Session replays"
                    canResizeColumns
                    size={InfiniteTableSize.MEDIUM}
                    data={filteredSessions}
                    getRowId={(row) => row.id}
                    columns={[
                      {
                        id: "started",
                        header: "Started (UTC)",
                        accessor: (row) => row.startedAt,

                        cell: ({ row }) => (
                          <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                            {formatInTimeZone(row.startedAt, "UTC", "MMM d, HH:mm:ss")}
                          </Text>
                        ),
                        isRowHeader: true,
                      },
                      {
                        id: "entry",
                        header: "Entry page",
                        accessor: (row) => row.entryPage,

                        cell: ({ row }) => (
                          <Text size={TextSize.BODY_SM} lineClamp={1}>
                            <DlsLink href={row.entryPage} size={LinkSize.SMALL} isExternal>
                              {row.entryPage}
                            </DlsLink>
                          </Text>
                        ),
                      },
                      {
                        id: "duration",
                        header: "Duration",
                        accessor: (row) => row.duration,
                        align: "right" as const,

                        cell: ({ row }) => (
                          <Text size={TextSize.BODY_SM}>{duration(row.duration)}</Text>
                        ),
                      },
                      {
                        id: "pageviews",
                        header: "Pageviews",
                        accessor: (row) => row.pageviews,
                        align: "right" as const,
                      },
                      {
                        id: "clicks",
                        header: "Clicks",
                        accessor: (row) => row.clicks,
                        align: "right" as const,
                      },
                      {
                        id: "replay",
                        header: "Replay",

                        cell: ({ row }) =>
                          row.replayUrl ? (
                            <Button
                              label="Watch replay"
                              size={ButtonSize.SMALL}
                              variant={ButtonVariant.SECONDARY}
                              href={row.replayUrl}
                              isExternal
                            />
                          ) : (
                            <Text size={TextSize.BODY_SM}>
                              {sessions.replayError ? "Unavailable" : "No recording"}
                            </Text>
                          ),
                      },
                    ]}
                    emptyState={
                      <EmptyState
                        header={
                          sessions.rows.length
                            ? "No sessions match these filters"
                            : "No sessions in this period"
                        }
                        description="Adjust the filters or time range to explore other sessions."
                      />
                    }
                  />
                </Box>
              </TableLayout>
            </TableBody>
          )}
        </Widget>
      ) : (
        <Grid>
          <BreakdownCard title="Top pages" rows={overview.pages} />
          <BreakdownCard title="Top referrers" rows={overview.referrers} />
          <BreakdownCard title="Top campaigns" rows={overview.campaigns} />
          <Widget
            isFlush
            header={
              <Text weight={TextWeight.MEDIUM} as="h2">
                Daily visitor activity
              </Text>
            }
          >
            <TableBody>
              <TableLayout
                footer={
                  <TableFooter label="Daily visitor activity" total={overview.trend.length} />
                }
              >
                <Box variant={BoxVariant.BASE} height="100%">
                  <InfiniteTable
                    ariaLabel="Daily website activity"
                    canResizeColumns
                    size={InfiniteTableSize.MEDIUM}
                    data={[...overview.trend].reverse()}
                    getRowId={(day) => day.day}
                    columns={[
                      {
                        id: "day",
                        header: "Date",
                        accessor: (day) => day.day,

                        cell: ({ row }) => (
                          <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                            {formatInTimeZone(row.day, "UTC", "MMM d, yyyy")}
                          </Text>
                        ),
                        isRowHeader: true,
                      },
                      {
                        id: "visitors",
                        header: "Visitors",
                        accessor: (day) => day.visitors,
                        align: "right" as const,
                      },

                      {
                        id: "pageviews",
                        header: "Pageviews",
                        accessor: (day) => day.pageviews,
                        align: "right" as const,
                      },
                    ]}
                  />
                </Box>
              </TableLayout>
            </TableBody>
          </Widget>
        </Grid>
      )}
    </Dashboard>
  );
}
function BreakdownCard({ title, rows }: { title: string; rows: WebsiteOverview["pages"] }) {
  return (
    <Widget
      isFlush
      header={
        <Text weight={TextWeight.MEDIUM} as="h2">
          {title}
        </Text>
      }
    >
      <TableBody>
        <TableLayout footer={<TableFooter label={title} total={rows.length} />}>
          <Box variant={BoxVariant.BASE} height="100%">
            <InfiniteTable
              ariaLabel={title}
              canResizeColumns
              size={InfiniteTableSize.MEDIUM}
              data={rows}
              getRowId={(row) => row.label}
              columns={[
                {
                  id: "label",
                  header: title,
                  accessor: (row) => row.label,

                  cell: ({ row }) => (
                    <div title={row.label}>
                      <Text size={TextSize.BODY_SM} lineClamp={1}>
                        {row.label}
                      </Text>
                    </div>
                  ),
                  isRowHeader: true,
                },
                {
                  id: "pageviews",
                  header: "Pageviews",
                  accessor: (row) => row.pageviews,
                  align: "right" as const,
                },
              ]}
              emptyState={<EmptyState header="No pageviews in this period" />}
            />
          </Box>
        </TableLayout>
      </TableBody>
    </Widget>
  );
}
